(ns metabase.lib-metric.core
  "Library for constructing and manipulating metric definitions.

   A MetricDefinition represents an exploration of a metric or measure, specifying:
   - A source: either a metric (saved card) or measure (reusable aggregation)
   - Filters: constraints on dimension values
   - Projections: dimensions to group by (become MBQL breakouts)

   Dimensions are UUID-referenced columns available for filtering and grouping.
   They provide stable identifiers that survive query changes, unlike field refs
   or column aliases. Dimension refs `[:dimension {} \"uuid\"]` are resolved to
   concrete field refs when the definition is converted to an executable MBQL query."
  (:require
   #?@(:clj [[metabase.lib-metric.ast.plan :as ast.plan]
             [metabase.lib-metric.dimension.jvm :as lib-metric.dimension.jvm]
             [metabase.lib-metric.metadata.jvm :as lib-metric.metadata.jvm]
             [potemkin :as p]]
       :cljs [[metabase.lib-metric.metadata.js :as lib-metric.metadata.js]])
   [metabase.lib-metric.clause :as lib-metric.clause]
   [metabase.lib-metric.definition :as lib-metric.definition]
   [metabase.lib-metric.dimension :as lib-metric.dimension]
   [metabase.lib-metric.measures :as lib-metric.measures]
   [metabase.lib-metric.metadata.provider :as lib-metric.metadata.provider]
   [metabase.lib-metric.metrics :as lib-metric.metrics]
   [metabase.lib-metric.projection :as lib-metric.projection]
   [metabase.lib-metric.types.isa :as lib-metric.types.isa]))

;; Ensure multimethod implementations are loaded
(comment lib-metric.dimension/keep-me
         lib-metric.measures/keep-me
         lib-metric.metrics/keep-me
         lib-metric.metadata.provider/keep-me
         lib-metric.projection/keep-me
         #?(:clj lib-metric.metadata.jvm/keep-me
            :cljs lib-metric.metadata.js/keep-me))

#?(:clj
   (p/import-vars
    [lib-metric.clause
     remove-clause
     replace-clause
     swap-clauses]
    [ast.plan
     join-and-compute]
    [lib-metric.definition
     expression-leaf-id
     expression-leaf-type
     expression-leaf-uuid
     expression-leaves
     filters
     from-measure-metadata
     from-metric-metadata
     projection-valid?
     projections
     source-measure-id
     source-metric-id
     unprojected-sources
     ->query-plan]
    [lib-metric.dimension
     add-dimensions
     addable-pairs
     dimension
     dimension-target->field-id
     dimensionable-query
     dimensions-changed?
     dimensions-for-measure
     dimensions-for-metric
     dimensions-for-table
     extract-persisted-dimensions
     field-ref->key
     get-persisted-dimension-mappings
     get-persisted-dimensions
     group-by-source
     main-group?
     mappings-changed?
     reconcile-dimensions-and-mappings
     reconcile-existing-dimensions
     remove-dimensions
     reorder-dimensions
     same-source?
     set-default-dimension
     update-dimension
     resolve-dimension-to-field-id]
    [lib-metric.dimension.jvm
     compute-dimension-pairs]
    [lib-metric.metadata.provider
     database-provider-for-table
     metric-context-metadata-provider]
    [lib-metric.metadata.jvm
     metadata-provider]
    [lib-metric.projection
     add-projection-positions
     default-breakout-dimensions
     dimension-breakout
     project-dimension
     projectable-dimensions
     valid-temporal-unit-for-type?]
    [lib-metric.types.isa
     type-isa?])
   :cljs
   (do
     (def remove-clause "See [[lib-metric.clause/remove-clause]]." lib-metric.clause/remove-clause)
     (def replace-clause "See [[lib-metric.clause/replace-clause]]." lib-metric.clause/replace-clause)
     (def swap-clauses "See [[lib-metric.clause/swap-clauses]]." lib-metric.clause/swap-clauses)
     (def expression-leaf-id "See [[lib-metric.definition/expression-leaf-id]]." lib-metric.definition/expression-leaf-id)
     (def expression-leaf-type "See [[lib-metric.definition/expression-leaf-type]]." lib-metric.definition/expression-leaf-type)
     (def expression-leaf-uuid "See [[lib-metric.definition/expression-leaf-uuid]]." lib-metric.definition/expression-leaf-uuid)
     (def expression-leaves "See [[lib-metric.definition/expression-leaves]]." lib-metric.definition/expression-leaves)
     (def filters "See [[lib-metric.definition/filters]]." lib-metric.definition/filters)
     (def from-measure-metadata "See [[lib-metric.definition/from-measure-metadata]]." lib-metric.definition/from-measure-metadata)
     (def from-metric-metadata "See [[lib-metric.definition/from-metric-metadata]]." lib-metric.definition/from-metric-metadata)
     (def projections "See [[lib-metric.definition/projections]]." lib-metric.definition/projections)
     (def source-measure-id "See [[lib-metric.definition/source-measure-id]]." lib-metric.definition/source-measure-id)
     (def source-metric-id "See [[lib-metric.definition/source-metric-id]]." lib-metric.definition/source-metric-id)
     (def dimension "See [[lib-metric.dimension/dimension]]." lib-metric.dimension/dimension)
     (def dimension-target->field-id "See [[lib-metric.dimension/dimension-target->field-id]]." lib-metric.dimension/dimension-target->field-id)
     (def dimensionable-query "See [[lib-metric.dimension/dimensionable-query]]." lib-metric.dimension/dimensionable-query)
     (def dimensions-for-measure "See [[lib-metric.dimension/dimensions-for-measure]]." lib-metric.dimension/dimensions-for-measure)
     (def dimensions-for-metric "See [[lib-metric.dimension/dimensions-for-metric]]." lib-metric.dimension/dimensions-for-metric)
     (def dimensions-for-table "See [[lib-metric.dimension/dimensions-for-table]]." lib-metric.dimension/dimensions-for-table)
     (def get-persisted-dimensions "See [[lib-metric.dimension/get-persisted-dimensions]]." lib-metric.dimension/get-persisted-dimensions)
     (def get-persisted-dimension-mappings "See [[lib-metric.dimension/get-persisted-dimension-mappings]]." lib-metric.dimension/get-persisted-dimension-mappings)
     (def group-by-source "See [[lib-metric.dimension/group-by-source]]." lib-metric.dimension/group-by-source)
     (def resolve-dimension-to-field-id "See [[lib-metric.dimension/resolve-dimension-to-field-id]]." lib-metric.dimension/resolve-dimension-to-field-id)
     (def same-source? "See [[lib-metric.dimension/same-source?]]." lib-metric.dimension/same-source?)
     (def field-ref->key "See [[lib-metric.dimension/field-ref->key]]." lib-metric.dimension/field-ref->key)
     (def database-provider-for-table "See [[lib-metric.metadata.provider/database-provider-for-table]]." lib-metric.metadata.provider/database-provider-for-table)
     (def metric-context-metadata-provider "See [[lib-metric.metadata.provider/metric-context-metadata-provider]]." lib-metric.metadata.provider/metric-context-metadata-provider)
     (def metadata-provider "See [[lib-metric.metadata.js/metadata-provider]]." lib-metric.metadata.js/metadata-provider)
     (def add-projection-positions "See [[lib-metric.projection/add-projection-positions]]." lib-metric.projection/add-projection-positions)
     (def default-breakout-dimensions "See [[lib-metric.projection/default-breakout-dimensions]]." lib-metric.projection/default-breakout-dimensions)
     (def dimension-breakout "See [[lib-metric.projection/dimension-breakout]]." lib-metric.projection/dimension-breakout)
     (def project-dimension "See [[lib-metric.projection/project-dimension]]." lib-metric.projection/project-dimension)
     (def projectable-dimensions "See [[lib-metric.projection/projectable-dimensions]]." lib-metric.projection/projectable-dimensions)
     (def valid-temporal-unit-for-type? "See [[lib-metric.projection/valid-temporal-unit-for-type?]]." lib-metric.projection/valid-temporal-unit-for-type?)
     (def type-isa? "See [[lib-metric.types.isa/type-isa?]]." lib-metric.types.isa/type-isa?)))
